import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaLinkedin, FaGithub, FaPaperPlane } from "react-icons/fa";
import { FiCheck, FiAlertTriangle } from "react-icons/fi";
import emailjs from "@emailjs/browser";

const Contact = () => {
  const form = useRef();
  const [status, setStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "",
  });
  const [errors, setErrors] = useState({});

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const contactItemVariant = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  // Gestion des changements dans les champs du formulaire
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });

    // Effacer l'erreur lorsque l'utilisateur commence à taper
    if (errors[id]) {
      setErrors({
        ...errors,
        [id]: null,
      });
    }
  };

  // Valider le formulaire
  const validateForm = () => {
    const newErrors = {};

    // Validation du nom
    if (!formData.name.trim()) {
      newErrors.name = "Le nom est requis";
    }

    // Validation de l'email
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    // Validation du sujet
    if (!formData.subject.trim()) {
      newErrors.subject = "Le sujet est requis";
    }

    // Validation du message
    if (!formData.message.trim()) {
      newErrors.message = "Le message est requis";
    } else if (formData.message.length < 10) {
      newErrors.message = "Le message doit contenir au moins 10 caractères";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction d'envoi du formulaire avec EmailJS
  const sendEmail = (e) => {
    e.preventDefault();

    // Vérifie si le champ caché est rempli
    if (formData.website) {
      return; // Soumission suspecte
    }

    if (!validateForm()) {
      return;
    }

    setStatus("loading");

    emailjs
      .sendForm(
        "service_sw8yijb",
        "template_jsdps3c",
        form.current,
        "JhtQsiL65xhmXzxK4"
      )
      .then((result) => {
        console.log("Email envoyé avec succès:", result.text);
        setStatus("success");

        // Réinitialiser le formulaire
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
          website: "",
        });
        form.current.reset();

        // Effacer le statut après 5 secondes
        setTimeout(() => {
          setStatus(null);
        }, 5000);
      })
      .catch((error) => {
        console.error("Erreur lors de l'envoi:", error.text);
        setStatus("error");
      });
  };

  return (
    <div className="bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
            Contactez-moi
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Vous avez une question, une opportunité d'alternance ou souhaitez
            simplement discuter d'un projet ? N'hésitez pas à me contacter via
            le formulaire ci-dessous.
          </p>
        </motion.div>

        {/* Bento Grid Layout */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          {/* Contact Form Card - Spans 2 columns */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg md:col-span-2 overflow-hidden relative group"
            variants={fadeInUp}
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500 opacity-10 rounded-full blur-3xl group-hover:opacity-20 transition-opacity duration-500"></div>

            {/* Status message */}
            {status === "success" && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 bg-green-500 bg-opacity-20 rounded-lg p-4 flex items-center text-green-400"
              >
                <FiCheck className="mr-2 flex-shrink-0" />
                <span>
                  Votre message a été envoyé avec succès! Je vous répondrai dès
                  que possible.
                </span>
              </motion.div>
            )}

            {status === "error" && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 bg-red-500 bg-opacity-20 rounded-lg p-4 flex items-center text-red-400"
              >
                <FiAlertTriangle className="mr-2 flex-shrink-0" />
                <span>
                  Une erreur s'est produite lors de l'envoi de votre message.
                  Veuillez réessayer.
                </span>
              </motion.div>
            )}

            <form
              ref={form}
              onSubmit={sendEmail}
              className="relative space-y-6"
            >
              <input
                type="text"
                id="website"
                name="website"
                style={{ display: "none" }}
              />
              <motion.div variants={fadeInUp}>
                <label htmlFor="name" className="block mb-2 text-gray-300">
                  Nom
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full p-3 bg-gray-700 border ${
                    errors.name ? "border-red-500" : "border-gray-600"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white`}
                  placeholder="Votre nom"
                  disabled={status === "loading"}
                />
                {errors.name && (
                  <p className="mt-1 text-red-500 text-sm">{errors.name}</p>
                )}
              </motion.div>

              <motion.div variants={fadeInUp}>
                <label htmlFor="email" className="block mb-2 text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full p-3 bg-gray-700 border ${
                    errors.email ? "border-red-500" : "border-gray-600"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white`}
                  placeholder="email@exemple.com"
                  disabled={status === "loading"}
                />
                {errors.email && (
                  <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
                )}
              </motion.div>

              <motion.div variants={fadeInUp}>
                <label htmlFor="subject" className="block mb-2 text-gray-300">
                  Sujet
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full p-3 bg-gray-700 border ${
                    errors.subject ? "border-red-500" : "border-gray-600"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white`}
                  placeholder="Sujet de votre message"
                  disabled={status === "loading"}
                />
                {errors.subject && (
                  <p className="mt-1 text-red-500 text-sm">{errors.subject}</p>
                )}
              </motion.div>

              <motion.div variants={fadeInUp}>
                <label htmlFor="message" className="block mb-2 text-gray-300">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full p-3 bg-gray-700 border ${
                    errors.message ? "border-red-500" : "border-gray-600"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white`}
                  rows="5"
                  placeholder="Votre message"
                  disabled={status === "loading"}
                ></textarea>
                {errors.message && (
                  <p className="mt-1 text-red-500 text-sm">{errors.message}</p>
                )}
              </motion.div>

              <motion.button
                type="submit"
                className={`flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                  status === "loading"
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:from-blue-600 hover:to-purple-700"
                }`}
                variants={fadeInUp}
                whileHover={status !== "loading" ? { scale: 1.05 } : {}}
                whileTap={status !== "loading" ? { scale: 0.95 } : {}}
                disabled={status === "loading"}
              >
                {status === "loading" ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Envoyer
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Info Card */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg"
            variants={fadeInUp}
          >
            <h3 className="text-xl font-bold mb-6 text-white">
              Informations de contact
            </h3>

            <motion.div className="space-y-6" variants={stagger}>
              <motion.div
                className="flex items-start space-x-4"
                variants={contactItemVariant}
              >
                <div className="flex-shrink-0 bg-gray-700 p-3 rounded-lg">
                  <FaEnvelope className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-gray-300 mb-1">Email</h4>
                  <a
                    href="mailto:riocheclement@gmail.com"
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    riocheclement@gmail.com
                  </a>
                </div>
              </motion.div>

              <motion.div
                className="flex items-start space-x-4"
                variants={contactItemVariant}
              >
                <div className="flex-shrink-0 bg-gray-700 p-3 rounded-lg">
                  <FaLinkedin className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-gray-300 mb-1">LinkedIn</h4>
                  <a
                    href="https://linkedin.com/in/clément-rioche-3694921a1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Clément Rioche
                  </a>
                </div>
              </motion.div>

              <motion.div
                className="flex items-start space-x-4"
                variants={contactItemVariant}
              >
                <div className="flex-shrink-0 bg-gray-700 p-3 rounded-lg">
                  <FaGithub className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-gray-300 mb-1">GitHub</h4>
                  <a
                    href="https://github.com/RiocheClement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    RiocheClement
                  </a>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="mt-8 p-4 bg-gray-700 rounded-lg"
              variants={fadeInUp}
            >
              <p className="text-gray-300 text-sm">
                Je suis actuellement à la recherche d'opportunités me permettant
                d'évoluer professionnellement dans le domaine du web et plus
                généralement dans le domaine de l'informatique. N'hésitez pas à
                me contacter pour discuter de vos projets ou collaborations
                potentielles.
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
